import type { AgentRoutingSchema } from './get-agent-routing-schemas';
import { foldPlatformKey } from './platform-catalog';
import { toExternalRoutingConfig } from './pype-agent-routing';

/** The connection fields the payload decision reads back off an edit target. */
export interface EditingConnectionRouting {
  /** The platform key the connection is currently bound to. */
  platformId?: string | null;
  /** The stored routing value — `external_agent` for every external connector. */
  agentRouting?: string | null;
  /** The stored callback URL, required by the backend for `external_agent`. */
  agentRoutingUrl?: string | null;
}

export interface LiveAgentPlatformFormValues {
  name: string;
  /** The platform dropdown value — a platform key, persisted as `platformId`. */
  agentRouting: string;
  env: 'live' | 'sandbox';
  agentRoutingConfig?: Record<string, string>;
}

export interface LiveAgentPlatformPayload {
  name: string;
  platformId: string;
  agentRouting: string;
  agentRoutingConfig: Record<string, unknown>;
  /**
   * `null`, never `undefined`, whenever the connection should carry NO callback
   * URL — see the comment on the field below. `undefined` is dropped from the
   * mutation variables, which the backend reads as "leave the stored value".
   */
  agentRoutingUrl: string | null;
  env: 'live' | 'sandbox';
}

/**
 * Turn the Add/Edit form into the mutation payload.
 *
 * Extracted from the modal because the decision is not a one-liner: it branches
 * on `platformUnchanged` × "is the platform's schema present" × `isExternal`,
 * and those three answers determine three coupled fields (`agentRouting`,
 * `agentRoutingConfig`, `agentRoutingUrl`). Inside the component none of it was
 * reachable by a test — the modal imports design-system Lit elements (`Select`,
 * `SelectOption`, `GenericModal`), which crash the node/jsdom environment — so
 * a mutation to any of the three branches stayed green (PSTUDIO-4072 review).
 *
 * Two invariants it exists to hold:
 *
 * - **`platformId` is written RAW.** It is the dropdown's value, the key the
 *   table icon and the edit pre-fill read back, and what `platform-catalog`
 *   only ever renames for DISPLAY. The same raw value is what
 *   `toExternalRoutingConfig` stamps into `integration_name`, which the legacy
 *   Console reads.
 * - **An absent schema on an UNCHANGED platform means "trust what is stored".**
 *   The catalog can legitimately not contain the platform (the integration was
 *   removed on the platform side). Reading that absence as "this is an internal
 *   platform" would write `agentRouting` as the integration NAME (e.g. `Quiq`)
 *   instead of the `external_agent` enum and drop the required callback URL, so
 *   merely re-saving an untouched external connection would corrupt its
 *   routing.
 */
export const buildLiveAgentPlatformPayload = ({
  values,
  isEdit,
  editingConnection,
  selectedSchema,
  storedConfig,
}: {
  values: LiveAgentPlatformFormValues;
  isEdit: boolean;
  editingConnection: EditingConnectionRouting | undefined;
  selectedSchema: AgentRoutingSchema | undefined;
  storedConfig: Record<string, unknown>;
}): LiveAgentPlatformPayload => {
  // Only merge the stored blob for the SAME platform. Switching platform clears
  // the form's config, but the stored blob belongs to the old platform, so
  // merging it would carry e.g. Quiq's api_token into a Salesforce connection —
  // and the backend copies `settings` verbatim onto every bound pype as
  // `agent_routing_config`.
  // Folded on BOTH sides, never compared raw: `ps-select-option` rewrites a
  // space in its `value` to `_`, so re-picking the SAME row in the dropdown
  // hands back a key that differs from the stored `platformId` by separators
  // alone. Compared raw, that reads as a platform switch and throws away the
  // connection's stored config and callback URL on a save that changed nothing.
  const platformUnchanged =
    isEdit &&
    foldPlatformKey(values.agentRouting) ===
      foldPlatformKey(editingConnection?.platformId);

  const isExternal =
    platformUnchanged && !selectedSchema
      ? editingConnection?.agentRouting === 'external_agent'
      : !!selectedSchema?.isExternal;

  // Keys the form can't render (nested objects such as `integration_headers`)
  // survive an edit; the user's input wins.
  const editedConfig = {
    ...(platformUnchanged ? storedConfig : {}),
    ...(values.agentRoutingConfig ?? {}),
  };

  return {
    name: values.name,
    platformId: values.agentRouting,
    agentRouting: isExternal ? 'external_agent' : values.agentRouting,
    // External connectors also need the legacy `integration_name` /
    // `integration_headers` spelling — the old Console UI reads the connector
    // and its credentials from those, and nothing downstream derives them.
    agentRoutingConfig: isExternal
      ? toExternalRoutingConfig(editedConfig, values.agentRouting)
      : editedConfig,
    // `external_agent` is paired with `agent_routing_url` (the integration
    // callback URL). Fall back to the stored URL when the catalog entry has
    // none, so an edit can't blank out a working connector — but only for the
    // SAME platform, exactly like the config merge above. Across a platform
    // switch the stored URL belongs to the OLD connector, and pairing it with
    // the new `integration_name` would misroute that connector's callbacks.
    //
    // Every "no URL" case is `null`, never `undefined`, and that is the whole
    // point rather than a style choice: `updatePypeConnection` is a PARTIAL
    // update, and urql drops an `undefined` variable from the request body, so
    // the argument never reaches the resolver and the stored value survives.
    // Measured on main (2026-09-04): omitting the variable left a connection's
    // callback URL untouched, while an explicit `null` cleared it. So sending
    // `undefined` on a switch to a platform with no callback URL would leave
    // the OLD connector's URL in the database beside the NEW `integration_name`
    // — the exact misrouting this gate exists to prevent, and invisible, since
    // the URL is rendered nowhere in the UI. The same reasoning covers a switch
    // from an external platform to an internal one.
    //
    // `?.trim() ||`, not `??`: integration data is hand-typed on the platform
    // side, so an empty/whitespace `callbackURL` comes back as `''` rather than
    // null — with `??` that empty string WINS over the stored URL and ships as
    // the connector's callback, a field that is present and useless.
    agentRoutingUrl: isExternal
      ? selectedSchema?.callbackUrl?.trim() ||
        (platformUnchanged ? editingConnection?.agentRoutingUrl : null) ||
        null
      : null,
    env: values.env,
  };
};
